import {useRouter} from "next/router";
import {useTranslation} from "@i18n/client";
import React, {PropsWithChildren, useCallback} from "react";

import {I18nNamespace} from "@i18n";
import {
  buildBookingLink,
  parseBookingQuery,
  useCurrentFlowId,
} from "../../../../page-containers/booking/utils/bookingQuery";
import {LocationFragment} from "../../../../queries/useFetchSlotsInArea";
import {CustomCategory, EventCategory, EventType} from "../../../../utils/analytics/constants";
import {getEventQueuer} from "../../../../utils/analytics/eventQueue";
import {getGoogleMapsDirections} from "../../../../utils/getGoogleMapDirections";
import {getApiUrl} from "src/constants/apiUrls";
import Button from "../../Button";
import Icon from "../../Icon";

type FollowUpButtonTextProps = {
  classes?: string;
};

const FollowUpButtonText = React.memo(
  ({children, classes = ""}: PropsWithChildren<FollowUpButtonTextProps>) => (
    <span className={`text-sm font-i font-semibold ${classes}`}>{children}</span>
  ),
);

FollowUpButtonText.displayName = "FollowUpButtonText";

type FollowUpButtonProps = {
  onClick?: () => void;
  href?: string;
  newTab?: boolean;
};

const FollowUpButton = React.memo(
  ({onClick, href, children, newTab}: PropsWithChildren<FollowUpButtonProps>) => {
    return (
      <Button onClick={onClick} href={href} newTab={newTab} variant="ghost" size="xs">
        <FollowUpButtonText>{children}</FollowUpButtonText>
      </Button>
    );
  },
);

FollowUpButton.displayName = "FollowUpButton";

type FollowUpButtonsProps = {
  location?: LocationFragment;
  viewId?: string;
  appointmentId?: string;
};

const FollowUpButtons = ({location, viewId, appointmentId}: FollowUpButtonsProps) => {
  const directionsUrl =
    location?.address && getGoogleMapsDirections(location.address, location.googlePlaceId);
  const router = useRouter();
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const flowId = useCurrentFlowId();
  const postEvent = getEventQueuer({
    category: EventCategory.Custom,
    extraData: {
      category: CustomCategory.AppointmentScheduling,
      flowId,
    },
  });

  const cancelText = i18n.t("Cancel", {
    ns: "web-scheduling",
  });
  const rescheduleText = i18n.t("Reschedule", {
    ns: "web-scheduling",
  });

  const onReschedule = useCallback(() => {
    postEvent({
      typeId: EventType.RescheduleApptClicked,
      extraData: {
        flowId,
        value: rescheduleText,
        viewId,
      },
    });

    const query = parseBookingQuery(router.query);

    router.push(
      buildBookingLink({
        ...query,
        selectedTime: undefined,
        selectedSlotIds: undefined,
        rescheduling: true,
      }),
      undefined,
      {shallow: true},
    );
  }, [flowId, postEvent, rescheduleText, router, viewId]);

  const onCancel = useCallback(async () => {
    postEvent({
      typeId: EventType.CancelApptClicked,
      extraData: {
        flowId,
        value: cancelText,
        viewId,
      },
    });
    router.push(
      buildBookingLink({
        ...parseBookingQuery(router.query),
        canceling: true,
      }),
      undefined,
      {shallow: true},
    );
  }, [cancelText, flowId, postEvent, router, viewId]);

  const icsUrl = `${getApiUrl()}/hib/appts/${appointmentId}/ics`;

  return (
    <div className="mt-8 flex gap-2 justify-center" data-cy="follow-up-buttons">
      <FollowUpButton href={icsUrl}>
        <Icon icon="calendar-add" className="text-xs sm:mr-1" />
        <FollowUpButtonText classes="hidden sm:inline-block">
          {i18n.t("Add to calendar", {ns: "web-scheduling"})}
        </FollowUpButtonText>
      </FollowUpButton>

      {directionsUrl && (
        <FollowUpButton href={directionsUrl} newTab>
          <Icon icon="map-marker-linear" className="text-xs sm:mr-1" />
          <FollowUpButtonText classes="hidden sm:inline-block">
            {i18n.t("Get Directions", {ns: "web-scheduling"})}
          </FollowUpButtonText>
        </FollowUpButton>
      )}
      <FollowUpButton onClick={onReschedule}>
        <FollowUpButtonText>{rescheduleText}</FollowUpButtonText>
      </FollowUpButton>
      <FollowUpButton onClick={onCancel}>
        <FollowUpButtonText>{cancelText}</FollowUpButtonText>
      </FollowUpButton>
    </div>
  );
};

export default React.memo(FollowUpButtons);
